import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useAppDispatch } from 'store/hooks';
import { showToast } from 'store/toastSlice';
import { Box, Typography, CircularProgress, TextField, Button } from '@mui/material';
import { ListFilter, ArrowRight } from 'lucide-react';
import ProviderSelect from 'components/common/ProviderSelect/ProviderSelect';
import {
  DndContext,
  rectIntersection,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  useDroppable,
} from '@dnd-kit/core';
import GameCard, { Game } from '../GameComposer/GameCard';
import { gameCategoryService } from 'components/api/gameCategories';
import { bonusFreeSpinsService } from 'components/api/bonusFreeSpins';
import styles from './BonusFreeSpinsTab.module.scss';

interface SubProvider {
  id: number;
  name: string;
  displayName?: string;
  providerId: number;
  providerCode: string;
  isEnabled: boolean;
  image: string | null;
}

const DroppableZone: React.FC<{ id: string; children: React.ReactNode; className?: string }> = ({
  id,
  children,
  className,
}) => {
  const { setNodeRef } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={className} id={id}>
      {children}
    </div>
  );
};

interface BonusFreeSpinsTabProps {
  siteId: string | number;
}

const BonusFreeSpinsTab: React.FC<BonusFreeSpinsTabProps> = ({ siteId }) => {
  const dispatch = useAppDispatch();
  const [providers, setProviders] = useState<SubProvider[]>([]);
  const [selectedProviderId, setSelectedProviderId] = useState<number | string>('');

  const [availableGames, setAvailableGames] = useState<Game[]>([]);
  const [gamesPage, setGamesPage] = useState<number>(1);
  const [hasMoreGames, setHasMoreGames] = useState<boolean>(true);
  const [isGamesLoading, setIsGamesLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [isSearchMode, setIsSearchMode] = useState<boolean>(false);

  const [freeSpinsGames, setFreeSpinsGames] = useState<Game[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCatLoading, setIsCatLoading] = useState<boolean>(true);

  const [activeGame, setActiveGame] = useState<Game | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  const selectedProvider = useMemo(() => {
    return providers.find((p) => p.id === Number(selectedProviderId));
  }, [providers, selectedProviderId]);

  const fetchProviders = useCallback(async () => {
    try {
      const res = await gameCategoryService.getSubProviders();
      const list = res?.data || res || [];
      setProviders(list);
      if (list.length > 0) {
        setSelectedProviderId(list[0].id);
      }
    } catch (err) {
      console.error('Failed to load sub-providers', err);
      dispatch(showToast({ text: 'Failed to load providers', type: 'error' }));
    }
  }, [siteId, dispatch]);

  const fetchFreeSpinsGames = useCallback(async () => {
    try {
      setIsCatLoading(true);
      const res = await bonusFreeSpinsService.getFreeSpinsGames(siteId);
      const list = res?.data || res || [];
      setFreeSpinsGames(list.map((g: any) => ({ ...g, image: g.imageUrl || g.image })));
    } catch (err) {
      console.error('Failed to load free spins games', err);
      dispatch(showToast({ text: 'Failed to load free spins games', type: 'error' }));
    } finally {
      setIsCatLoading(false);
    }
  }, [siteId, dispatch]);

  const fetchAvailableGames = useCallback(
    async (provId: number | string, page: number, append = false, searchVal = searchValue) => {
      if (!provId) return;
      try {
        setIsGamesLoading(true);
        const res = await gameCategoryService.getGamesBySubProvider({
          subProviderId: provId,
          page,
          pageSize: 16,
          siteId,
          search: isSearchMode && searchVal ? searchVal : undefined,
          hasFreeSpins: true,
        });

        const items = res?.data?.items || [];
        const formatted = items.map((game: any) => ({
          id: String(game.id),
          title: game.title,
          provider: game.producer,
          image: game.imageUrl || game.image,
        }));

        if (append) {
          setAvailableGames((prev) => {
            const filteredPrev = prev.filter(
              (existing) => !formatted.find((f: any) => f.id === existing.id),
            );
            return [...filteredPrev, ...formatted];
          });
        } else {
          setAvailableGames(formatted);
          if (scrollRef.current) scrollRef.current.scrollTop = 0;
        }
        setHasMoreGames(items.length === 16);
      } catch (err) {
        console.error('Failed to load games', err);
      } finally {
        setIsGamesLoading(false);
      }
    },
    [siteId, isSearchMode, searchValue],
  );

  useEffect(() => {
    fetchProviders();
    fetchFreeSpinsGames();
  }, [fetchProviders, fetchFreeSpinsGames]);

  useEffect(() => {
    if (selectedProviderId) {
      setGamesPage(1);
      fetchAvailableGames(selectedProviderId, 1, false, '');
    }
  }, [selectedProviderId]);

  const handleProviderChange = (providerId: string | number) => {
    setSelectedProviderId(providerId);
    setSearchValue('');
    setIsSearchMode(false);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight * 1.5 && hasMoreGames && !isGamesLoading) {
      const nextPage = gamesPage + 1;
      setGamesPage(nextPage);
      fetchAvailableGames(selectedProviderId, nextPage, true);
    }
  };

  useEffect(() => {
    if (!isSearchMode) return;

    const handler = setTimeout(() => {
      setGamesPage(1);
      fetchAvailableGames(selectedProviderId, 1, false, searchValue);
    }, 400);

    return () => {
      clearTimeout(handler);
    };
  }, [searchValue, isSearchMode, selectedProviderId]);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const activeIdStr = String(active.id).replace('category-item-', '');
    const game =
      availableGames.find((g) => String(g.id) === activeIdStr) ||
      freeSpinsGames.find((g) => String(g.id) === activeIdStr);
    if (game) setActiveGame(game);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveGame(null);
    if (!over) return;

    if (over.id === 'category-zone' || String(over.id).startsWith('category-item-')) {
      const activeIdStr = String(active.id).replace('category-item-', '');
      const isAlreadyInCategory = freeSpinsGames.some((g) => String(g.id) === activeIdStr);

      if (isAlreadyInCategory) {
        const oldIndex = freeSpinsGames.findIndex((g) => String(g.id) === activeIdStr);
        let newIndex = freeSpinsGames.length;
        if (String(over.id).startsWith('category-item-')) {
          const overIdStr = String(over.id).replace('category-item-', '');
          newIndex = freeSpinsGames.findIndex((g) => String(g.id) === overIdStr);
        }

        if (oldIndex !== newIndex && newIndex !== -1) {
          const newOrder = [...freeSpinsGames];
          const [movedItem] = newOrder.splice(oldIndex, 1);
          newOrder.splice(newIndex, 0, movedItem);
          setFreeSpinsGames(newOrder);
          setHasChanges(true);
        }
      } else {
        if (freeSpinsGames.length >= 3) {
          dispatch(showToast({ text: 'Maximum of 3 games allowed', type: 'error' }));
          return;
        }

        const gameToAdd = availableGames.find((g) => String(g.id) === activeIdStr);
        if (gameToAdd) {
          setFreeSpinsGames((prev) => [gameToAdd, ...prev]);
          setHasChanges(true);
        }
      }
    }
  };

  const removeGameFromCategory = (gameId: number | string) => {
    setFreeSpinsGames((prev) => prev.filter((g) => String(g.id) !== String(gameId)));
    setHasChanges(true);
  };

  const handleSaveOrder = async () => {
    if (freeSpinsGames.length < 3) {
      dispatch(showToast({ text: 'Please select exactly 3 games before saving.', type: 'error' }));
      return;
    }

    try {
      setIsSaving(true);
      await bonusFreeSpinsService.saveFreeSpinsGames(siteId, freeSpinsGames);
      setHasChanges(false);
      dispatch(showToast({ text: 'Bonus Free Spins games saved successfully!', type: 'success' }));
    } catch (err) {
      console.error('Failed to save games', err);
      dispatch(showToast({ text: 'Failed to save games', type: 'error' }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <Typography variant="h5" className={styles.title}>
            Bonus Free Spins Games
          </Typography>
          <Typography variant="body2" sx={{ color: '#8698a6', mt: 0.5 }}>
            You can select a maximum of 3 games for this bonus. Drag and drop from the left panel to
            right.
          </Typography>
        </Box>
        {hasChanges && (
          <Button
            variant="contained"
            className={styles.saveOrderBtn}
            onClick={handleSaveOrder}
            disabled={isSaving}
            sx={{
              ml: 'auto',
              backgroundColor: 'var(--accent-blue)',
              color: '#fff',
              '&:hover': { backgroundColor: '#0099cc' },
            }}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Box className={styles.mainLayout}>
          {/* Left Panel: Available Games */}
          <Box className={styles.panel}>
            <Box className={styles.panelHeader}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography className={styles.panelTitle}>Available Games</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <ProviderSelect
                      providers={providers}
                      selectedProviderId={selectedProviderId}
                      onChange={handleProviderChange}
                      className={styles.providerSelect}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Search game..."
                      value={searchValue}
                      onChange={(e) => {
                        setSearchValue(e.target.value);
                        if (!isSearchMode) setIsSearchMode(true);
                      }}
                      className={styles.searchInput}
                      autoComplete="off"
                      fullWidth
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box className={styles.panelContent} onScroll={handleScroll} ref={scrollRef}>
              {availableGames.map((game) => (
                <GameCard
                  key={`available-${game.id}`}
                  game={game}
                  isSortable
                  isDisabled={freeSpinsGames.some((g) => String(g.id) === String(game.id))}
                />
              ))}
              {isGamesLoading && (
                <Box className={styles.loaderBox}>
                  <CircularProgress size={30} sx={{ color: 'var(--accent-blue)' }} />
                </Box>
              )}
              {!isGamesLoading && availableGames.length === 0 && (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>No games found.</Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Right Panel: Bonus Free Spins Games */}
          <Box className={styles.panel}>
            <Box className={styles.panelHeader}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box className={styles.categoryIconBox}>
                    <ListFilter size={20} />
                  </Box>
                  <Box>
                    <Typography className={styles.panelTitle}>Bonus Free Spins Games</Typography>
                    <Typography className={styles.panelSubtitle}>
                      {freeSpinsGames.length} / 3 Games added
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
            <DroppableZone
              id="category-zone"
              className={`${styles.panelContent} ${freeSpinsGames.length === 0 ? styles.emptyZone : ''}`}
            >
              {isCatLoading ? (
                <Box className={styles.loaderBox}>
                  <CircularProgress size={30} sx={{ color: 'var(--accent-blue)' }} />
                </Box>
              ) : (
                <>
                  {freeSpinsGames.map((game) => (
                    <GameCard
                      key={`category-${game.id}`}
                      game={game}
                      isSortable
                      dndId={`category-item-${game.id}`}
                      onRemove={() => removeGameFromCategory(game.id)}
                    />
                  ))}
                  {freeSpinsGames.length === 0 && (
                    <Box className={styles.emptyPlaceholder}>
                      <ArrowRight size={32} />
                      <Typography>Drag games here</Typography>
                    </Box>
                  )}
                </>
              )}
            </DroppableZone>
          </Box>
        </Box>

        <DragOverlay zIndex={1500} dropAnimation={null}>
          {activeGame ? <GameCard game={activeGame} /> : null}
        </DragOverlay>
      </DndContext>
    </Box>
  );
};

export default BonusFreeSpinsTab;
